import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Toaster, toast } from 'sonner';
import {
  Star, ArrowLeft, CircleNotch, DownloadSimple, CaretUp, CaretDown,
  ArrowsOutLineHorizontal, Target, ArrowsLeftRight,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';

import { useDrawsUpdated } from '../hooks/useDrawsUpdated';
import { NarrativeIntroBlock, NarrativeObservationsBlock, NarrativeKeyFindingsBlock, ReportInaccuracyLink } from '../components/WindowNarrative';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/* ================================================================
 * W8 Column Glossary — used inside the Introduction block.
 * A single source-of-truth for every column shown in the tables.
 * ================================================================ */
const W8_COLUMN_GLOSSARY = [
  { group: 'Core Before/After counts', color: 'bg-blue-100 text-blue-900', items: [
    { k: 'Before', v: 'Number of times this candidate historically appeared in the draw IMMEDIATELY BEFORE the current (latest) number at this ball position.' },
    { k: 'After',  v: 'Number of times this candidate historically appeared in the draw IMMEDIATELY AFTER the current number at this ball position.' },
    { k: 'Diff',   v: 'Before − After. Positive = tends to precede; negative = tends to follow the current number.' },
    { k: 'Total',  v: 'Before + After. Interaction strength between the candidate and the current number.' },
    { k: 'Before %', v: 'Before ÷ Σ(all Before counts) — share of the “before” column.' },
    { k: 'After %',  v: 'After ÷ Σ(all After counts) — share of the “after” column (used as a baseline probability).' },
  ]},
  { group: 'Recency signals', color: 'bg-orange-100 text-orange-900', items: [
    { k: 'Recent', v: 'How many of the LAST 10 historical occurrences of the current number were followed by this candidate. RELATIONSHIP-based — sliced from the After column, restricted to the last 10 times the current number appeared.' },
    { k: 'R25',    v: 'How many times this candidate appeared AT THIS BALL POSITION in the LAST 25 DRAWS. UNCONDITIONAL — has nothing to do with the current number; pure ball-position recency.' },
  ]},
  { group: 'W3 — Ball-to-Ball Difference cross-reference', color: 'bg-teal-100 text-teal-900', items: [
    { k: 'W3 d',   v: 'Difference = candidate − current number. This is the “gap” between draws in the W3 methodology.' },
    { k: 'W3 %',   v: 'Historical probability (%) of that exact difference occurring at this ball position across the full dataset.' },
    { k: '75%',    v: 'Y/N badge: is the candidate inside the 94% coverage range of historical W3 differences? Numbers outside are statistical outliers.' },
  ]},
  { group: 'W6 — Monthly Frequency cross-reference', color: 'bg-amber-100 text-amber-900', items: [
    { k: 'W6 %',   v: 'W6 Overall % = candidate count at this ball position ÷ total draws. All-time positional frequency.' },
    { k: 'M.Df',   v: 'W6 Month Diff = W6 Overall % − W6 Current-Month %. POSITIVE = under-represented in the current month → overdue this month. NEGATIVE = over-represented → cooling off.' },
    { k: 'Abs',    v: 'W6 Absent Years = consecutive most-recent years in which the candidate did NOT appear at this ball position during the current month. Higher = longer month-drought.' },
    { k: 'CY',     v: 'W6 Current-Year count = how many times the candidate has been drawn at this ball position THIS calendar year (any month). 0 = not yet seen this year at this ball.' },
  ]},
  { group: 'Structural / Rarity signals', color: 'bg-violet-100 text-violet-900', items: [
    { k: 'AfGap',  v: 'After-Gap = how many appearances of the current number have passed since this candidate last followed it. Larger = the candidate is more overdue as a follower.' },
    { k: 'NDef',   v: 'Neighbor Deficit = average total interaction of neighbours (n−2, n−1, n+1, n+2) MINUS this candidate’s total. Detects a “hole” in an otherwise active neighbourhood.' },
    { k: 'Seq',    v: 'Sequence Count = how many times the exact chain (prev draw’s number → current number → THIS candidate) has been observed historically at this ball position.' },
    { k: 'Uni',    v: 'UNI = Unicorn (Total = 0 yet the candidate has overall positional frequency > 0 → never interacted with the current number). RARE = Total ≤ 3.' },
  ]},
];

function ColumnGlossaryPanel() {
  return (
    <details className="mt-4 rounded-lg border border-sky-300 bg-white/70" data-testid="w8-column-glossary">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-black text-sky-900 uppercase tracking-wider hover:bg-sky-50 rounded-lg">
        📖 Column Glossary — click to expand definitions of every column
      </summary>
      <div className="p-4 pt-2 space-y-4">
        {W8_COLUMN_GLOSSARY.map((grp) => (
          <div key={grp.group}>
            <p className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-2">{grp.group}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {grp.items.map((it) => (
                <div key={it.k} className="flex gap-2 items-start bg-white rounded border border-gray-200 p-2">
                  <span className={`shrink-0 inline-flex items-center rounded px-2 py-0.5 text-[11px] font-black font-mono ${grp.color}`}>{it.k}</span>
                  <span className="text-[12px] text-gray-800 leading-snug">{it.v}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="rounded-md bg-rose-50 border border-rose-200 p-3">
          <p className="text-[12px] text-rose-900"><strong>Recent vs R25 — the critical distinction:</strong></p>
          <ul className="text-[12px] text-rose-900 mt-1 space-y-1 list-disc list-inside">
            <li><strong>Recent</strong> is <em>relationship-based</em>: only the last 10 times the current number appeared, how many were followed by this candidate. If Recent = 0 for a candidate with high After, it&apos;s cooling as a follower.</li>
            <li><strong>R25</strong> is <em>unconditional</em>: total appearances of the candidate in the ball position across the very last 25 draws — regardless of what came before. Measures raw positional freshness.</li>
            <li>They can disagree: a number may be “hot in R25” yet “cold in Recent” — that means it&apos;s active generally but avoids following the current number specifically.</li>
          </ul>
        </div>
      </div>
    </details>
  );
}

/* ================================================================
 * W8 Introduction block — sits above the Tabs.
 * ================================================================ */
function W8Introduction({ data }) {
  const latest = data?.latest_draw;
  const latestDate = data?.latest_date;
  const totalDraws = data?.total_draws;
  const balls = data?.balls || {};
  const currents = Object.entries(balls)
    .map(([k, b]) => ({ pos: k.replace('ball_', ''), num: b?.current_number }))
    .filter(x => x.num != null);

  return (
    <div className="bg-gradient-to-r from-sky-50 via-blue-50 to-indigo-50 border-2 border-sky-300 rounded-xl p-5" data-testid="w8-introduction">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex w-10 h-10 rounded-full bg-sky-600 text-white items-center justify-center font-black text-base">i</div>
        <div className="flex-1">
          <h2 className="text-base font-black text-sky-900 uppercase tracking-wider">Introduction — What Window 8 analyses</h2>
          <p className="text-sm text-sky-800 mt-1.5 mb-3">
            Window 8 is the <strong>Before &amp; After Engine</strong>. For each of the five main-ball positions it locks onto the number drawn in the very latest draw
            {latest ? <> (<strong>#{latest}</strong>{latestDate ? ` on ${latestDate}` : ''})</> : ''}, then walks the entire {totalDraws ? `${totalDraws.toLocaleString()}-draw` : 'full'} history and asks a single question per candidate <em>n</em> ∈ [1, 50]:
            <em> “How often did n appear in the draw immediately <strong>BEFORE</strong> vs immediately <strong>AFTER</strong> the current number at this ball position?”</em>
          </p>
          <ol className="list-decimal list-inside space-y-1.5 text-sm text-gray-800 marker:font-black marker:text-sky-700">
            <li>
              <strong>Current anchors</strong>: {currents.length ? currents.map((c, i) => (
                <span key={c.pos} className="inline-flex items-center gap-1 mx-0.5">
                  <span className="text-xs text-gray-500">Ball {c.pos} =</span>
                  <span className="inline-flex w-6 h-6 rounded-full bg-sky-600 text-white items-center justify-center font-mono font-black text-xs">{c.num}</span>
                  {i < currents.length - 1 ? ',' : ''}
                </span>
              )) : '—'}. Every table below is computed relative to these five anchors.
            </li>
            <li>
              <strong>Cross-referenced signals</strong>: W8 does not read Before/After alone — it fuses <strong>W3</strong> (Ball-to-Ball Difference probability &amp; 94% range), <strong>W6</strong> (Monthly Frequency: overall %, month bias, absent years, current-year count), <strong>R25</strong> (last-25-draws positional recency), and structural signals <strong>AfGap, NDef, Seq</strong>, and rarity flags <strong>UNI/RARE</strong>.
            </li>
            <li>
              <strong>Prediction score</strong>: for each ball, the top-10 panel ranks candidates by a weighted sum of the signals (After-drought, B/A-reversion, W6 month bias &amp; absence, R25, AfGap, Neighbor Deficit / Contrast, Sequence Reversion, Cross-Position Pressure, Interaction Deficit). Higher score = higher predicted probability of appearing NEXT at that ball position.
            </li>
            <li>
              <strong>Auto-refresh</strong>: when a new draw is entered, all five anchors, glossaries, and rankings recalculate automatically.
            </li>
          </ol>
          <ColumnGlossaryPanel />
          <ReportInaccuracyLink block="introduction" windowId="W8" accentColor="text-sky-700 hover:text-sky-900" />
        </div>
      </div>
    </div>
  );
}

/* ================================================================
 * W8 Observations — Key findings (auto-computed across the 5 balls)
 * ================================================================ */
function W8Observations({ data }) {
  const balls = data?.balls || {};
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  const perBall = ballKeys.map((bk) => {
    const b = balls[bk];
    if (!b) return null;
    const nums = b.numbers || [];
    const topAfter = [...nums].sort((a, z) => (z.after || 0) - (a.after || 0))[0];
    const topBefore = [...nums].sort((a, z) => (z.before || 0) - (a.before || 0))[0];
    const maxDiffPos = [...nums].sort((a, z) => (z.diff || 0) - (a.diff || 0))[0];
    const unicorns = nums.filter(n => n.is_unicorn);
    const highAfGap = [...nums].filter(n => n.after > 0).sort((a, z) => (z.after_gap || 0) - (a.after_gap || 0))[0];
    return { pos: bk.replace('ball_', ''), cur: b.current_number, topAfter, topBefore, maxDiffPos, unicorns, highAfGap };
  }).filter(Boolean);

  const totalUnicorns = perBall.reduce((s, pb) => s + (pb.unicorns?.length || 0), 0);
  const strongestFollower = [...perBall].sort((a, z) => (z.topAfter?.after || 0) - (a.topAfter?.after || 0))[0];
  const biggestPreOnly = [...perBall].sort((a, z) => (z.maxDiffPos?.diff || 0) - (a.maxDiffPos?.diff || 0))[0];

  return (
    <NarrativeObservationsBlock
      windowId="W8"
      testid="w8-observations"
      body={<>
        Auto-generated summary of the strongest historical patterns detected across the 5 ball positions relative to the latest draw
        {data?.latest_draw ? <> (<strong>#{data.latest_draw}</strong>)</> : ''}.
      </>}
      bullets={[
        strongestFollower?.topAfter && strongestFollower.topAfter.after > 0 && {
          content: <><strong>Strongest natural follower</strong>: Ball {strongestFollower.pos} — after <strong>{strongestFollower.cur}</strong>, number <strong>{strongestFollower.topAfter.number}</strong> has followed it <strong>{strongestFollower.topAfter.after}× ({(strongestFollower.topAfter.after_pct * 100).toFixed(1)}% of all follow-ups)</strong>. This is the highest empirical follow-up rate across all 5 balls.</>,
        },
        biggestPreOnly?.maxDiffPos && biggestPreOnly.maxDiffPos.diff > 0 && {
          content: <><strong>Biggest &ldquo;precedes-but-never-follows&rdquo; anomaly</strong>: Ball {biggestPreOnly.pos} — number <strong>{biggestPreOnly.maxDiffPos.number}</strong> shows Diff = <strong>+{biggestPreOnly.maxDiffPos.diff}</strong> (Before {biggestPreOnly.maxDiffPos.before}× vs After {biggestPreOnly.maxDiffPos.after}×) relative to current <strong>{biggestPreOnly.cur}</strong>. High reversion candidate.</>,
        },
        totalUnicorns > 0 && {
          content: <><strong>{totalUnicorns} UNICORN candidate{totalUnicorns > 1 ? 's' : ''}</strong> detected across the 5 balls — numbers with non-zero W6 overall frequency that have <em>never</em> interacted (Before = After = 0) with the current anchor at that ball. These are structural gaps in the pattern.</>,
        },
        ...perBall.map((pb) => pb.topAfter && pb.topAfter.after > 0 && {
          content: <><strong>Ball {pb.pos} (current = {pb.cur})</strong>: Max After → <strong>#{pb.topAfter.number}</strong> ({pb.topAfter.after}×), Max Before → <strong>#{pb.topBefore?.number}</strong> ({pb.topBefore?.before}×){pb.highAfGap ? <>, biggest AfGap → <strong>#{pb.highAfGap.number}</strong> ({pb.highAfGap.after_gap} appearances since last follow-up)</> : ''}{pb.unicorns?.length ? <>, {pb.unicorns.length} unicorn{pb.unicorns.length > 1 ? 's' : ''}</> : ''}.</>,
        }),
      ].filter(Boolean)}
      footer="Bold names are the empirical extremes; combine them with the Prediction Score panel further down for the model's synthesised pick."
    />
  );
}

/* ================================================================
 * W8 Key Findings — applicable to the next draw
 * ================================================================ */
function W8KeyFindings({ data }) {
  const balls = data?.balls || {};
  const ballKeys = ['ball_1', 'ball_2', 'ball_3', 'ball_4', 'ball_5'];

  const picks = ballKeys.map((bk) => {
    const b = balls[bk];
    if (!b) return null;
    const nums = b.numbers || [];
    const top = [...nums].sort((a, z) => (z.pred_score || 0) - (a.pred_score || 0))[0];
    if (!top) return null;

    // Determine dominant signal from the top pick
    const signals = [];
    if (top.is_unicorn) signals.push('UNICORN (never interacted with current)');
    if (top.after === 0 && top.w6_overall_pct > 0) signals.push('after-drought');
    if (top.recent_after === 0 && top.after > 0) signals.push('recent absence');
    if (top.w6_month_diff > 2) signals.push(`month bias (+${top.w6_month_diff}%)`);
    if (top.w6_absent_years >= 3) signals.push(`${top.w6_absent_years}yr month-drought`);
    if (top.recent_25 === 0) signals.push('cold in last 25 draws');
    if (top.after_gap >= 10) signals.push(`AfGap ${top.after_gap}`);
    if (top.seq_count > 0) signals.push(`seq-reversion (${top.seq_count}×)`);
    if ((top.cross_pressure || 0) > 0) signals.push(`cross-position pressure ${top.cross_pressure}×`);
    if ((top.interaction_deficit || 0) > 0) signals.push(`interaction deficit ${top.interaction_deficit}`);

    return { pos: bk.replace('ball_', ''), cur: b.current_number, top, signals };
  }).filter(Boolean);

  const ticket = picks.map(p => p.top.number).sort((a, b) => a - b);

  return (
    <NarrativeKeyFindingsBlock
      windowId="W8"
      testid="w8-key-findings"
      title="Key Findings — applicable to the next draw"
      body={<>Synthesised recommendation blending After/Before, W3, W6, R25 and structural signals. Below is the top pick per ball, followed by the composed ticket.</>}
      badge={ticket.length === 5 ? { color: 'from-emerald-600 to-teal-700', icon: '🎯', label: 'W8 composed ticket', detail: ticket.join(' · ') } : null}
      bullets={picks.map((p) => ({
        content: <>
          <strong>Ball {p.pos}</strong> (anchor = <strong>{p.cur}</strong>) → top pick <span className="inline-flex w-7 h-7 rounded-full bg-emerald-600 text-white items-center justify-center font-mono font-black text-xs mx-1">{p.top.number}</span>
          <span className="text-xs text-gray-600 font-mono">
            score {p.top.pred_score} · After {p.top.after}× ({(p.top.after_pct * 100).toFixed(1)}%) · W3 d={p.top.w3_diff > 0 ? '+' : ''}{p.top.w3_diff} ({p.top.w3_pct}%) {p.top.w3_in_range ? '✓' : '✗'} · W6 {p.top.w6_overall_pct}% / M.Df {p.top.w6_month_diff > 0 ? '+' : ''}{p.top.w6_month_diff}% · Abs {p.top.w6_absent_years}y · CY {p.top.w6_cy_count} · R25 {p.top.recent_25}
          </span>
          {p.signals.length > 0 && (
            <div className="text-xs text-emerald-900 mt-1 ml-6">Dominant signals: {p.signals.join(' · ')}.</div>
          )}
        </>,
      }))}
      footer="These are model-suggested candidates only; consult W53 (Ball Master) or W62 (Consolidated Verdict) for the cross-window consensus."
    />
  );
}


const ResizableHeader = ({ children, width, onResize, columnKey, onClick, className = '' }) => {
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const handleMouseDown = (e) => {
    e.preventDefault(); e.stopPropagation();
    startXRef.current = e.clientX; startWidthRef.current = width;
    const move = (ev) => { onResize(columnKey, Math.max(35, startWidthRef.current + (ev.clientX - startXRef.current))); };
    const up = () => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); document.body.style.cursor = ''; document.body.style.userSelect = ''; };
    document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    document.body.style.cursor = 'col-resize'; document.body.style.userSelect = 'none';
  };
  return (
    <th className={`relative ${className}`} style={{ width: `${width}px`, minWidth: `${width}px`, maxWidth: `${width}px` }} onClick={onClick}>
      <div className="flex items-center justify-center gap-1 pr-2 font-bold">{children}</div>
      <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500/30 group flex items-center justify-center" onMouseDown={handleMouseDown} onClick={(e) => e.stopPropagation()}>
        <div className="w-0.5 h-4 bg-gray-300 group-hover:bg-blue-500 transition-colors" />
      </div>
    </th>
  );
};

const BallTable = ({ numbersData, ballData, sortConfig, onSort, colWidths, onResize }) => {
  const sorted = [...numbersData].sort((a, b) => {
    let aVal = a[sortConfig.key] || 0;
    let bVal = b[sortConfig.key] || 0;
    return sortConfig.dir === 'desc' ? bVal - aVal : aVal - bVal;
  });
  const topPredicted = new Set([...numbersData].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0)).slice(0, 5).map(n => n.number));
  const w3 = ballData.w3_range || {};

  const SortIcon = ({ k }) => {
    if (sortConfig.key !== k) return null;
    return sortConfig.dir === 'desc' ? <CaretDown size={10} /> : <CaretUp size={10} />;
  };

  const hCls = "px-1 py-2 text-[10px] uppercase tracking-wider text-gray-700 cursor-pointer hover:bg-gray-200 text-center";
  const cur = ballData.current_number;

  return (
    <div className="overflow-x-auto" data-testid="before-after-table">
      <table className="w-full text-xs" style={{ tableLayout: 'fixed', minWidth: '600px' }}>
        <thead className="bg-gray-100">
          <tr>
            <ResizableHeader width={colWidths.num} onResize={onResize} columnKey="num" onClick={() => onSort('number')} className={hCls}>
              # <SortIcon k="number" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.before} onResize={onResize} columnKey="before" onClick={() => onSort('before')} className={`${hCls} bg-blue-100`}>
              Before {cur} <SortIcon k="before" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.after} onResize={onResize} columnKey="after" onClick={() => onSort('after')} className={`${hCls} bg-green-100`}>
              After {cur} <SortIcon k="after" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.diff} onResize={onResize} columnKey="diff" onClick={() => onSort('diff')} className={`${hCls} bg-amber-100`}>
              Diff <SortIcon k="diff" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.total} onResize={onResize} columnKey="total" onClick={() => onSort('total')} className={`${hCls} bg-purple-100`}>
              Total <SortIcon k="total" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.apct} onResize={onResize} columnKey="apct" onClick={() => onSort('after_pct')} className={`${hCls} bg-green-50`}>
              After% <SortIcon k="after_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.bpct} onResize={onResize} columnKey="bpct" onClick={() => onSort('before_pct')} className={`${hCls} bg-blue-50`}>
              Before% <SortIcon k="before_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.recent} onResize={onResize} columnKey="recent" onClick={() => onSort('recent_after')} className={`${hCls} bg-orange-100`}>
              Recent <SortIcon k="recent_after" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w3d || 45} onResize={onResize} columnKey="w3d" onClick={() => onSort('w3_diff')} className={`${hCls} bg-teal-100`}>
              W3 d <SortIcon k="w3_diff" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w3p || 50} onResize={onResize} columnKey="w3p" onClick={() => onSort('w3_pct')} className={`${hCls} bg-teal-100`}>
              W3 % <SortIcon k="w3_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w3r || 35} onResize={onResize} columnKey="w3r" className={`${hCls} bg-teal-100`}>
              75%
            </ResizableHeader>
            <ResizableHeader width={colWidths.w6o || 45} onResize={onResize} columnKey="w6o" onClick={() => onSort('w6_overall_pct')} className={`${hCls} bg-amber-100`}>
              W6% <SortIcon k="w6_overall_pct" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w6m || 45} onResize={onResize} columnKey="w6m" onClick={() => onSort('w6_month_diff')} className={`${hCls} bg-amber-100`}>
              M.Df <SortIcon k="w6_month_diff" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w6a || 35} onResize={onResize} columnKey="w6a" onClick={() => onSort('w6_absent_years')} className={`${hCls} bg-amber-100`}>
              Abs <SortIcon k="w6_absent_years" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.w6c || 30} onResize={onResize} columnKey="w6c" className={`${hCls} bg-amber-100`}>
              CY
            </ResizableHeader>
            <ResizableHeader width={colWidths.r25 || 35} onResize={onResize} columnKey="r25" onClick={() => onSort('recent_25')} className={`${hCls} bg-rose-100`}>
              R25 <SortIcon k="recent_25" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.agap || 45} onResize={onResize} columnKey="agap" onClick={() => onSort('after_gap')} className={`${hCls} bg-violet-100`}>
              AfGap <SortIcon k="after_gap" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.ndef || 45} onResize={onResize} columnKey="ndef" onClick={() => onSort('neighbor_deficit')} className={`${hCls} bg-violet-100`}>
              NDef <SortIcon k="neighbor_deficit" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.seq || 40} onResize={onResize} columnKey="seq" onClick={() => onSort('seq_count')} className={`${hCls} bg-violet-100`}>
              Seq <SortIcon k="seq_count" />
            </ResizableHeader>
            <ResizableHeader width={colWidths.uni || 35} onResize={onResize} columnKey="uni" className={`${hCls} bg-yellow-100`}>
              Uni
            </ResizableHeader>
          </tr>
        </thead>
        <tbody>
          { sorted.map((row, idx) => {
            const maxAfter = Math.max(...numbersData.map(n => n.after));
            const isTopAfter = row.after === maxAfter && maxAfter > 0;
            return (
              <tr key={row.number} className={`${topPredicted.has(row.number) ? 'bg-blue-100' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isTopAfter ? 'ring-2 ring-green-400 bg-green-50' : ''}`} style={topPredicted.has(row.number) ? {borderLeft: '4px solid #2563eb', backgroundColor: 'rgba(37, 99, 235, 0.18)'} : {}} data-testid={`row-${row.number}`}>
                <td className="px-1 py-1 border-b border-gray-100 font-mono font-bold text-center">{row.number}</td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-blue-50/30 ${row.before > 0 ? 'font-bold' : 'text-gray-300'}`}>
                  {row.before || '-'}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center bg-green-50/30 ${row.after > 0 ? 'font-bold' : 'text-gray-300'}`}>
                  {row.after || '-'}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center font-bold ${row.diff > 0 ? 'text-blue-700' : row.diff < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                  {row.diff > 0 ? '+' : ''}{row.diff}
                </td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center font-bold bg-purple-50/30">
                  {row.total || '-'}
                </td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-green-50/10">
                  {(row.after_pct * 100).toFixed(1)}%
                </td>
                <td className="px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-blue-50/10">
                  {(row.before_pct * 100).toFixed(1)}%
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center ${row.recent_after > 0 ? 'font-bold text-orange-600' : 'text-gray-300'}`}>
                  {row.recent_after || '-'}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-teal-50/40 ${row.w3_in_range ? '' : 'text-red-400'}`}>
                  {row.w3_diff > 0 ? '+' : ''}{row.w3_diff}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-teal-50/40 ${row.w3_pct >= 3 ? 'font-bold text-teal-700' : row.w3_pct >= 2 ? 'text-teal-600' : 'text-gray-400'}`}>
                  {row.w3_pct}%
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 text-center bg-teal-50/40`}>
                  {row.w3_in_range ? <span className="text-[9px] bg-green-100 text-green-700 px-1 rounded font-bold">Y</span> : <span className="text-[9px] bg-red-100 text-red-600 px-1 rounded font-bold">N</span>}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-amber-50/40 ${row.w6_overall_pct >= 4 ? 'font-bold' : ''}`}>
                  {row.w6_overall_pct}%
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-amber-50/40 font-bold ${row.w6_month_diff > 2 ? 'text-red-600' : row.w6_month_diff > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {row.w6_month_diff > 0 ? '+' : ''}{row.w6_month_diff}%
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-amber-50/40 ${row.w6_absent_years >= 5 ? 'font-bold text-red-600' : row.w6_absent_years >= 3 ? 'text-orange-600' : ''}`}>
                  {row.w6_absent_years}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-amber-50/40 ${row.w6_cy_count === 0 ? 'font-bold text-red-600' : 'text-green-600'}`}>
                  {row.w6_cy_count}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-rose-50/40 font-bold ${row.recent_25 === 0 ? 'text-red-600' : row.recent_25 <= 1 ? 'text-orange-600' : 'text-gray-600'}`}>
                  {row.recent_25}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-violet-50/40 font-bold ${row.after_gap >= 20 ? 'text-red-600' : row.after_gap >= 10 ? 'text-orange-600' : 'text-gray-600'}`}>
                  {row.after_gap}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-violet-50/40 font-bold ${row.neighbor_deficit > 5 ? 'text-red-600' : row.neighbor_deficit > 0 ? 'text-amber-600' : 'text-gray-400'}`}>
                  {row.neighbor_deficit > 0 ? row.neighbor_deficit : '-'}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 font-mono text-center text-[10px] bg-violet-50/40 font-bold ${row.seq_count > 0 ? 'text-violet-700' : 'text-gray-300'}`}>
                  {row.seq_count || '-'}
                </td>
                <td className={`px-1 py-1 border-b border-gray-100 text-center bg-yellow-50/40`}>
                  {row.is_unicorn ? <span className="text-[9px] bg-yellow-100 text-yellow-700 px-1 rounded font-bold">UNI</span> : row.is_rare ? <span className="text-[9px] bg-orange-100 text-orange-700 px-1 rounded font-bold">RARE</span> : <span className="text-gray-300">-</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const defaultWidths = { num: 42, before: 75, after: 75, diff: 55, total: 55, apct: 55, bpct: 55, recent: 55, w3d: 45, w3p: 50, w3r: 35, agap: 45, ndef: 45, seq: 40 };

export default function Window8BeforeAfter() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'after', dir: 'desc' });
  const [colWidths, setColWidths] = useState({ ...defaultWidths });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/before-after`);
      setData(res.data);
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);
  useDrawsUpdated(fetchData);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const res = await axios.get(`${API}/analysis/before-after/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', `before_after_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Export completed');
    } catch { toast.error('Failed to export'); }
    finally { setExportLoading(false); }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/')} className="text-gray-500 hover:text-gray-700" data-testid="back-btn"><ArrowLeft size={18} /></Button>
              <div className="w-10 h-10 bg-[#002FA7] rounded flex items-center justify-center"><Star size={24} weight="fill" className="text-amber-400" /></div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>Window 8</h1>
                <p className="text-xs text-gray-500">Before & After Analysis (Latest Draw)</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={() => { setColWidths({ ...defaultWidths }); toast.success('Reset'); }} variant="ghost" size="sm" className="text-gray-500"><ArrowsOutLineHorizontal size={18} /></Button>
              <Button onClick={handleExport} disabled={exportLoading} className="btn-secondary flex items-center gap-2" data-testid="export-btn">
                {exportLoading ? <CircleNotch size={18} className="animate-spin" /> : <DownloadSimple size={18} />}
                Export Excel
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>
        ) : data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Total Draws</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.total_draws}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4">
                <p className="text-xs uppercase tracking-wider font-bold text-gray-500">Latest Draw</p>
                <p className="text-2xl font-bold text-gray-900 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>#{data.latest_draw}</p>
              </div>
              <div className="bg-white border border-gray-200 rounded p-4 bg-amber-50 border-amber-200">
                <p className="text-xs uppercase tracking-wider font-bold text-amber-600">Latest Date</p>
                <p className="text-xl font-bold text-amber-700 mt-1" style={{ fontFamily: 'var(--font-heading)' }}>{data.latest_date}</p>
              </div>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4 flex items-center gap-3">
              <ArrowsLeftRight size={20} className="text-indigo-600" />
              <p className="text-sm text-indigo-700">
                Analysis is based on the <strong>latest draw</strong>. For each ball position, it shows how often each number appeared <strong>before</strong> and <strong>after</strong> the current number in historical data. <strong>Automatically updates</strong> when a new draw is entered.
              </p>
            </div>

            <div className="mb-6">
              <W8Introduction data={data} />
            </div>

            <Tabs defaultValue="ball_1" className="space-y-4">
              <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
                {Object.entries(ballLabels).map(([key, label]) => {
                  const bd = data.balls?.[key];
                  return (
                    <TabsTrigger key={key} value={key} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold" data-testid={`tab-${key}`}>
                      {label} = {bd?.current_number || '?'}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {Object.entries(ballLabels).map(([key, label]) => {
                const ballData = data.balls?.[key] || {};
                const numbersData = ballData.numbers || [];
                const cur = ballData.current_number;
                const topAfter = [...numbersData].sort((a, b) => b.after - a.after)[0];
                const topBefore = [...numbersData].sort((a, b) => b.before - a.before)[0];
                const maxDiffPos = [...numbersData].sort((a, b) => b.diff - a.diff)[0];
                const maxDiffNeg = [...numbersData].sort((a, b) => a.diff - b.diff)[0];

                return (
                  <TabsContent key={key} value={key}>
                    {/* W3 75% Range Banner */}
                    {ballData.w3_range && (
                      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 rounded-lg px-5 py-3 mb-4 text-white" data-testid={`w3-range-${key}`}>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg">W3 Difference Range</span>
                            <span className="text-sm opacity-80">{label} = {cur} → 75% of next draws fall within:</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="bg-white/20 rounded px-3 py-1.5 font-mono font-bold text-lg">d = {ballData.w3_range.range_75_min_diff > 0 ? '+' : ''}{ballData.w3_range.range_75_min_diff} to {ballData.w3_range.range_75_max_diff > 0 ? '+' : ''}{ballData.w3_range.range_75_max_diff}</span>
                            <span className="bg-white/20 rounded px-3 py-1.5 font-mono font-bold text-lg">Range {ballData.w3_range.range_75_min_num} - {ballData.w3_range.range_75_max_num}</span>
                            <span className="bg-white/15 rounded px-2 py-1.5 font-mono font-bold text-sm">{ballData.w3_range.range_75_pct}%</span>
                          </div>
                        </div>
                        {ballData.w3_range.top_diffs && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {ballData.w3_range.top_diffs.slice(0, 8).map(td => (
                              <span key={td.diff} className="bg-white/10 rounded px-2 py-1 text-xs font-mono">
                                d={td.diff > 0 ? '+' : ''}{td.diff}→{td.next_value} <span className="font-bold">{td.pct}%</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Sequence Analysis Panel */}
                    {ballData.sequence_analysis && (
                      <div className="bg-gradient-to-r from-violet-600 to-purple-700 rounded-lg px-5 py-3 mb-4 text-white" data-testid={`seq-panel-${key}`}>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-lg">Sequence Analysis</span>
                            <span className="text-sm opacity-80">After {ballData.sequence_analysis.prev_number} → {ballData.sequence_analysis.current_number}, what comes next?</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="bg-white/20 rounded px-3 py-1.5 font-mono font-bold text-lg">{ballData.sequence_analysis.total_occurrences}x seen</span>
                            <span className="bg-white/15 rounded px-2 py-1.5 font-mono text-sm">
                              ≤{cur}: {ballData.sequence_analysis.below_current} ({ballData.sequence_analysis.below_pct}%) | &gt;{cur}: {ballData.sequence_analysis.above_current} ({ballData.sequence_analysis.above_pct}%)
                            </span>
                            <span className="bg-white/15 rounded px-2 py-1.5 font-mono text-sm">
                              1-9: {ballData.sequence_analysis.single_digit} ({ballData.sequence_analysis.single_pct}%) | 10+: {ballData.sequence_analysis.double_digit} ({ballData.sequence_analysis.double_pct}%)
                            </span>
                          </div>
                        </div>
                        {/* Reversion Insight */}
                        <div className="flex gap-3 mt-2 flex-wrap items-center">
                          {ballData.sequence_analysis.reversion_favors !== 'balanced' && (
                            <span className="bg-yellow-400/25 text-yellow-100 rounded px-3 py-1 text-xs font-bold" data-testid={`seq-reversion-${key}`}>
                              REVERSION → {ballData.sequence_analysis.reversion_favors === 'above' ? `Above ${cur}` : `Below ${cur}`} overdue ({ballData.sequence_analysis.reversion_favors === 'above' ? ballData.sequence_analysis.below_pct : ballData.sequence_analysis.above_pct}% dominated)
                            </span>
                          )}
                          {ballData.sequence_analysis.digit_reversion !== 'balanced' && (
                            <span className="bg-yellow-400/25 text-yellow-100 rounded px-3 py-1 text-xs font-bold">
                              {ballData.sequence_analysis.digit_reversion === 'double' ? 'Double-digit' : 'Single-digit'} overdue ({ballData.sequence_analysis.digit_reversion === 'double' ? ballData.sequence_analysis.single_pct : ballData.sequence_analysis.double_pct}% dominated)
                            </span>
                          )}
                          {ballData.sequence_analysis.reversion_favors === 'balanced' && ballData.sequence_analysis.digit_reversion === 'balanced' && (
                            <span className="bg-white/10 rounded px-3 py-1 text-xs">No dominant pattern — balanced distribution</span>
                          )}
                        </div>
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {ballData.sequence_analysis.numbers.map((sn, si) => (
                            <span key={sn.number} className={`rounded px-2 py-1 text-xs font-mono ${si === 0 ? 'bg-yellow-400/30 text-yellow-100 font-bold' : 'bg-white/10'}`}>
                              →{sn.number} <span className="font-bold">{sn.count}x</span>
                              {ballData.sequence_analysis.total_occurrences > 0 && (
                                <span className="opacity-70 ml-1">({Math.round(sn.count / ballData.sequence_analysis.total_occurrences * 100)}%)</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {ballData.prev_number !== null && ballData.prev_number !== undefined && !ballData.sequence_analysis && (
                      <div className="bg-violet-50 border border-violet-200 rounded-lg px-5 py-3 mb-4 text-violet-700 text-sm" data-testid={`seq-none-${key}`}>
                        Sequence {ballData.prev_number} → {cur}: No historical occurrences found — this is a new sequence.
                      </div>
                    )}
                    <div className="bg-white border border-gray-200 rounded overflow-hidden">
                      <div className="p-4 border-b border-gray-200">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <div>
                            <h3 className="font-bold text-gray-900" style={{ fontFamily: 'var(--font-heading)' }}>
                              {label} = <span className="text-2xl text-[#002FA7]">{cur}</span> — Before & After Analysis
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                              Number {cur} appeared <strong>{ballData.times_appeared}</strong> times in {label} position
                            </p>
                          </div>
                          <div className="flex gap-3 text-xs">
                            {topAfter && topAfter.after > 0 && (
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded font-bold">
                                Max After: #{topAfter.number} ({topAfter.after}x)
                              </span>
                            )}
                            {topBefore && topBefore.before > 0 && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded font-bold">
                                Max Before: #{topBefore.number} ({topBefore.before}x)
                              </span>
                            )}
                            {maxDiffPos && maxDiffPos.diff > 0 && (
                              <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded font-bold">
                                Max Diff: #{maxDiffPos.number} (+{maxDiffPos.diff})
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <BallTable
                        numbersData={numbersData}
                        ballData={ballData}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        colWidths={colWidths}
                        onResize={handleResize}
                      />
                    </div>

                    {/* Prediction Section */}
                    <div className="bg-gradient-to-r from-indigo-700 to-blue-800 rounded-lg p-5 mt-4 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <Target size={24} weight="bold" />
                        <h3 className="font-bold text-xl" style={{ fontFamily: 'var(--font-heading)' }}>
                          Predicted Next {label} (After {cur})
                        </h3>
                      </div>
                      <p className="text-xs text-white/70 mb-4">
                        Score = <strong className="text-green-300">AfDrought (3%) + LowTotal (3%)</strong> + Reversion (2%) + <strong className="text-green-300">B/A Rev (4%)</strong> + Absence (3%) + <strong className="text-teal-300">W3 diff (2%) + W3 range (2%)</strong> + <strong className="text-amber-300">W6 ovr (4%) + W6 bias (6%) + W6 abs (3%+) + W6 CY (3%)</strong> + <strong className="text-rose-300">R25 (4%)</strong> + <strong className="text-violet-300">AfGap (8%) + NDef+NContrast (6%) + SeqRev (8%) + Rarity (8%)</strong> + <strong className="text-orange-300">CP (10%)</strong> + <strong className="text-yellow-300">IDef (14%)</strong>
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs" data-testid="prediction-table">
                          <thead>
                            <tr className="text-white/70 border-b border-white/20">
                              <th className="px-2 py-2 text-left font-bold">#</th>
                              <th className="px-2 py-2 text-center font-bold">Number</th>
                              <th className="px-2 py-2 text-center font-bold">Before</th>
                              <th className="px-2 py-2 text-center font-bold">After</th>
                              <th className="px-2 py-2 text-center font-bold">Diff</th>
                              <th className="px-2 py-2 text-center font-bold">Total</th>
                              <th className="px-2 py-2 text-center font-bold">After%</th>
                              <th className="px-2 py-2 text-center font-bold">Recent</th>
                              <th className="px-2 py-2 text-center font-bold text-teal-300">W3 d</th>
                              <th className="px-2 py-2 text-center font-bold text-teal-300">W3 %</th>
                              <th className="px-2 py-2 text-center font-bold text-teal-300">75%</th>
                              <th className="px-2 py-2 text-center font-bold text-amber-300">W6%</th>
                              <th className="px-2 py-2 text-center font-bold text-amber-300">M.Df</th>
                              <th className="px-2 py-2 text-center font-bold text-amber-300">Abs</th>
                              <th className="px-2 py-2 text-center font-bold text-amber-300">CY</th>
                              <th className="px-2 py-2 text-center font-bold text-rose-300">R25</th>
                              <th className="px-2 py-2 text-center font-bold text-violet-300">AfGap</th>
                              <th className="px-2 py-2 text-center font-bold text-violet-300">NDef</th>
                              <th className="px-2 py-2 text-center font-bold text-violet-300">NC</th>
                              <th className="px-2 py-2 text-center font-bold text-violet-300">SeqRev</th>
                              <th className="px-2 py-2 text-center font-bold text-violet-300">Uni</th>
                              <th className="px-2 py-2 text-center font-bold text-orange-300">CP</th>
                              <th className="px-2 py-2 text-center font-bold text-yellow-300">IDef</th>
                              <th className="px-2 py-2 text-center font-bold">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {[...numbersData]
                              .sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0))
                              .slice(0, 10)
                              .map((n, idx) => (
                                <tr key={n.number} className={idx === 0 ? 'bg-white/20' : idx % 2 === 0 ? 'bg-white/5' : ''} data-testid={`pred-row-${n.number}`}>
                                  <td className="px-2 py-2 font-bold">{idx + 1}</td>
                                  <td className="px-2 py-2 text-center font-mono text-xl font-bold">{n.number}</td>
                                  <td className="px-2 py-2 text-center font-mono">{n.before}</td>
                                  <td className="px-2 py-2 text-center font-mono font-bold">{n.after}</td>
                                  <td className={`px-2 py-2 text-center font-mono font-bold ${n.diff > 0 ? 'text-blue-300' : n.diff < 0 ? 'text-red-300' : ''}`}>
                                    {n.diff > 0 ? '+' : ''}{n.diff}
                                  </td>
                                  <td className="px-2 py-2 text-center font-mono">{n.total}</td>
                                  <td className="px-2 py-2 text-center font-mono">{(n.after_pct * 100).toFixed(1)}%</td>
                                  <td className={`px-2 py-2 text-center font-mono font-bold ${n.recent_after === 0 ? 'text-yellow-300' : ''}`}>
                                    {n.recent_after}
                                  </td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] ${n.w3_in_range ? '' : 'text-red-300'}`}>
                                    {n.w3_diff > 0 ? '+' : ''}{n.w3_diff}
                                  </td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] ${n.w3_pct >= 3 ? 'font-bold text-teal-300' : ''}`}>
                                    {n.w3_pct}%
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    {n.w3_in_range ? <span className="text-[9px] bg-green-400/20 text-green-300 px-1 rounded font-bold">Y</span> : <span className="text-[9px] bg-red-400/20 text-red-300 px-1 rounded font-bold">N</span>}
                                  </td>
                                  <td className="px-2 py-2 text-center font-mono text-[10px]">{n.w6_overall_pct}%</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${n.w6_month_diff > 2 ? 'text-red-300' : n.w6_month_diff > 0 ? 'text-amber-300' : ''}`}>
                                    {n.w6_month_diff > 0 ? '+' : ''}{n.w6_month_diff}%
                                  </td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] ${n.w6_absent_years >= 5 ? 'font-bold text-red-300' : ''}`}>{n.w6_absent_years}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] ${n.w6_cy_count === 0 ? 'font-bold text-red-300' : ''}`}>{n.w6_cy_count}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${n.recent_25 === 0 ? 'text-red-300' : n.recent_25 <= 1 ? 'text-rose-300' : ''}`}>{n.recent_25}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${n.after_gap >= 20 ? 'text-red-300' : n.after_gap >= 10 ? 'text-violet-300' : ''}`}>{n.after_gap}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${n.neighbor_deficit > 5 ? 'text-red-300' : n.neighbor_deficit > 0 ? 'text-violet-300' : ''}`}>{n.neighbor_deficit > 0 ? n.neighbor_deficit : '-'}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${(n.neighbor_contrast || 0) >= 10 ? 'text-yellow-300 bg-yellow-900/20' : (n.neighbor_contrast || 0) > 0 ? 'text-violet-300' : 'text-white/30'}`}>{(n.neighbor_contrast || 0) > 0 ? n.neighbor_contrast : '-'}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${n.seq_count > 0 ? 'text-violet-300' : ''}`}>{n.seq_count || '-'}</td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold`}>
                                    {n.is_unicorn ? <span className="bg-yellow-400/30 text-yellow-200 px-1.5 rounded text-[9px]">UNI</span> : n.is_rare ? <span className="bg-orange-400/30 text-orange-200 px-1.5 rounded text-[9px]">RARE</span> : '-'}
                                  </td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${(n.cross_pressure || 0) >= 3 ? 'text-red-300 bg-red-900/20' : (n.cross_pressure || 0) > 0 ? 'text-orange-300' : 'text-white/30'}`}>
                                    {(n.cross_pressure || 0) > 0 ? n.cross_pressure : '-'}
                                  </td>
                                  <td className={`px-2 py-2 text-center font-mono text-[10px] font-bold ${(n.interaction_deficit || 0) >= 2 ? 'text-yellow-300 bg-yellow-900/20' : (n.interaction_deficit || 0) > 0 ? 'text-yellow-200' : 'text-white/30'}`}>
                                    {(n.interaction_deficit || 0) > 0 ? n.interaction_deficit : '-'}
                                  </td>
                                  <td className="px-2 py-2 text-center font-bold text-lg">{n.pred_score}</td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                      {(() => {
                        const top = [...numbersData].sort((a, b) => (b.pred_score || 0) - (a.pred_score || 0))[0];
                        return top ? (
                          <div className="mt-4 bg-sky-100 text-sky-900 rounded-lg p-3 flex items-center gap-3 flex-wrap">
                            <div className="text-xs font-bold px-2 py-1 bg-yellow-900 text-yellow-400 rounded">TOP PICK</div>
                            <span className="font-mono text-2xl font-bold">{top.number}</span>
                            <span className="font-bold text-sm">
                              After {cur}: {top.after}x ({(top.after_pct * 100).toFixed(1)}%) | W3 d={top.w3_diff > 0 ? '+' : ''}{top.w3_diff} ({top.w3_pct}%) {top.w3_in_range ? '✓' : '✗'} | W6 {ballData.w6_month}: {top.w6_overall_pct}% ovr, {top.w6_month_diff > 0 ? '+' : ''}{top.w6_month_diff}% diff, {top.w6_absent_years}yr abs, CY:{top.w6_cy_count} | R25:{top.recent_25} | AfGap:{top.after_gap} NDef:{top.neighbor_deficit > 0 ? top.neighbor_deficit : '—'} Seq:{top.seq_count || '—'} {top.is_unicorn ? '| UNICORN' : ''} {(top.cross_pressure || 0) > 0 ? `| CP:${top.cross_pressure}x` : ''} {(top.interaction_deficit || 0) > 0 ? `| IDef:${top.interaction_deficit} (exp:${top.expected_after})` : ''}
                            </span>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  </TabsContent>
                );
              })}
            </Tabs>

            <div className="mt-8 space-y-6">
              <W8Observations data={data} />
              <W8KeyFindings data={data} />
            </div>
          </>
        ) : null}
      </main>
    </div>
  );
}


export function Window8Content() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'after', dir: 'desc' });
  const [colWidths, setColWidths] = useState({ ...defaultWidths });
  const handleResize = (key, w) => setColWidths(prev => ({ ...prev, [key]: w }));

  useEffect(() => {
    (async () => {
      try { const res = await axios.get(`${API}/analysis/before-after`); setData(res.data); }
      catch { toast.error('Failed to fetch'); }
      finally { setLoading(false); }
    })();
  }, []);

  const handleSort = (key) => setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

  if (loading) return <div className="flex justify-center py-20"><CircleNotch size={32} className="animate-spin text-[#002FA7]" /></div>;
  if (!data) return null;

  return (
    <Tabs defaultValue="ball_1" className="space-y-4">
      <TabsList className="bg-white border border-gray-200 p-1 flex-wrap">
        {Object.entries(ballLabels).map(([key, label]) => (
          <TabsTrigger key={key} value={key} className="data-[state=active]:bg-[#002FA7] data-[state=active]:text-white font-bold">{label}</TabsTrigger>
        ))}
      </TabsList>
      {Object.entries(ballLabels).map(([key, label]) => {
        const ball = data.balls?.[key] || {};
        const numbersData = ball.numbers || [];
        return (
          <TabsContent key={key} value={key}>
            <BallTable numbersData={numbersData} ballData={ball} sortConfig={sortConfig} onSort={handleSort} colWidths={colWidths} onResize={handleResize} />
          </TabsContent>
        );
      })}
    </Tabs>
  );
}
